import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import axiosInstance from './axiosInstance';
import ProtectedComponent from './ProtectedComponent';
import { AuthContext } from './context/AuthContext';
import { getSocket, emitSocket } from './socket';
import './styles/DigitalSignageMaintenance.css';

function DigitalSignageMaintenance() {
  const { user } = useContext(AuthContext);

  // Selection state
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [lcdCompanies, setLcdCompanies] = useState([]); // Full company objects with LCD enabled
  const [loadingLcdCompanies, setLoadingLcdCompanies] = useState(true);

  // Data state
  const [summary, setSummary] = useState(null);
  const [screens, setScreens] = useState([]);
  const [scheduleAccuracySummary, setScheduleAccuracySummary] = useState(null);
  const [playbackStats, setPlaybackStats] = useState(null);  // Real-time playback verification stats
  const [scheduleIssuesSummary, setScheduleIssuesSummary] = useState(null);  // Schedule issues breakdown
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // UI state
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterContent, setFilterContent] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [lastUpdated, setLastUpdated] = useState(null);

  // Auto-refresh state - default ON
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(300); // seconds (5 min default)
  const [countdown, setCountdown] = useState(300);

  // Cache status state
  const [fromCache, setFromCache] = useState(false);
  const [cacheLastUpdated, setCacheLastUpdated] = useState(null);
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [justRefreshed, setJustRefreshed] = useState(false); // Track if data was just manually refreshed

  // Alert setup state
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertSettings, setAlertSettings] = useState({
    email: '',
    alertOffline: true,
    alertContentIssues: true,
    alertScheduleIssues: false,
    threshold: 1 // Number of issues before alerting
  });
  const [savingAlert, setSavingAlert] = useState(false);
  const [alertSaved, setAlertSaved] = useState(false);

  // Manage alerts state
  const [manageAlertsModalOpen, setManageAlertsModalOpen] = useState(false);
  const [userAlerts, setUserAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [deletingAlert, setDeletingAlert] = useState(null);

  // Critical issues alert state
  const [criticalAlertDismissed, setCriticalAlertDismissed] = useState(false);
  const [previousIssueCount, setPreviousIssueCount] = useState(null);
  const [newIssuesDetected, setNewIssuesDetected] = useState(false);

  // Modal state for summary card details
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null); // 'online', 'offline', 'content', 'schedule'
  const [modalTitle, setModalTitle] = useState('');

  // Ref to track current company for socket cleanup
  const currentCompanyRef = useRef(null);

  // Socket.IO integration for live updates
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    // Listen for digital signage updates
    const handleDigitalSignageUpdate = (data) => {
      console.log('[DigitalSignage] Received live update:', data);
      // Only update if this is for our current company
      if (data.company === currentCompanyRef.current) {
        if (data.screens) setScreens(data.screens);
        if (data.summary) setSummary(data.summary);
        if (data.scheduleAccuracySummary) setScheduleAccuracySummary(data.scheduleAccuracySummary);
        if (data.playbackStats) setPlaybackStats(data.playbackStats);
        setLastUpdated(new Date(data.timestamp));
      }
    };

    // Listen for LCD companies cache refresh
    const handleLcdCompaniesUpdated = (data) => {
      console.log('[DigitalSignage] LCD companies cache updated:', data);
      // Refresh the companies list
      fetchLcdCompanies();
    };

    socket.on('digital-signage:update', handleDigitalSignageUpdate);
    socket.on('lcd:companies:updated', handleLcdCompaniesUpdated);

    return () => {
      socket.off('digital-signage:update', handleDigitalSignageUpdate);
      socket.off('lcd:companies:updated', handleLcdCompaniesUpdated);
    };
  }, []);

  // Join/leave digital-signage room when company changes
  useEffect(() => {
    // Leave previous room
    if (currentCompanyRef.current) {
      emitSocket('digital-signage:leave', {});
    }

    // Join new room if company selected
    if (selectedCompany?.companyCode) {
      currentCompanyRef.current = selectedCompany.companyCode;
      emitSocket('digital-signage:join', {
        company: selectedCompany.companyCode,
        store: selectedStore || null
      });
    } else {
      currentCompanyRef.current = null;
    }

    // Cleanup on unmount
    return () => {
      if (currentCompanyRef.current) {
        emitSocket('digital-signage:leave', {});
        currentCompanyRef.current = null;
      }
    };
  }, [selectedCompany?.companyCode, selectedStore]);

  // Fetch LCD-enabled companies and global settings on mount
  useEffect(() => {
    fetchLcdCompanies();
    fetchGlobalAutoRefreshInterval();
  }, []);

  // Fetch global auto-refresh interval setting
  const fetchGlobalAutoRefreshInterval = async () => {
    try {
      const response = await axiosInstance.get('/digital-signage/settings/auto-refresh-interval');
      if (response.data.success && response.data.interval) {
        setAutoRefreshInterval(response.data.interval);
        setCountdown(response.data.interval);
      }
    } catch (err) {
      console.error('Error fetching auto-refresh interval:', err);
      // Use default 60s if fetch fails
    }
  };

  // Fetch companies that have LCD package enabled
  const fetchLcdCompanies = async () => {
    try {
      setLoadingLcdCompanies(true);
      const response = await axiosInstance.post('/digital-signage/companies');
      if (response.data.success) {
        // Store full company objects, filtered by user's access
        let companies = response.data.companies || [];

        // Filter by user's company access (unless super admin)
        if (!user?.isSuperAdmin && user?.companyAccess?.length > 0) {
          companies = companies.filter(c => user.companyAccess.includes(c.code));
        }

        setLcdCompanies(companies);
      }
    } catch (err) {
      console.error('Error fetching LCD companies:', err);
      setError('Failed to load LCD-enabled companies');
    } finally {
      setLoadingLcdCompanies(false);
    }
  };

  // Handle company selection from dropdown
  const handleCompanyChange = async (e) => {
    const companyCode = e.target.value;

    if (!companyCode) {
      setSelectedCompany(null);
      setStores([]);
      setSelectedStore('');
      setSummary(null);
      setScreens([]);
      setError('');
      return;
    }

    // Find company in lcdCompanies
    const company = lcdCompanies.find(c => c.code === companyCode);
    if (!company) return;

    setSelectedCompany({ companyCode: company.code, companyName: company.name });
    setSelectedStore('');
    setSummary(null);
    setScreens([]);
    setError('');

    // Fetch stores for this company
    try {
      const storesRes = await axiosInstance.get(`/function-assignment/proxy-stores?company=${companyCode}`);
      if (storesRes.data?.stores) {
        setStores(storesRes.data.stores);
      } else {
        setStores([]);
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
      setStores([]);
    }

    // Fetch signage data
    fetchData(companyCode);
  };

  // Fetch data (summary + screens)
  const fetchData = async (companyCode = null) => {
    const company = companyCode || selectedCompany?.companyCode;
    if (!company) return;

    setLoading(true);
    setError('');

    try {
      // Fetch summary and screens in parallel
      const [summaryRes, screensRes] = await Promise.all([
        axiosInstance.post('/digital-signage/summary', {
          company: company,
          store: selectedStore || undefined
        }),
        axiosInstance.post('/digital-signage/screens', {
          company: company,
          store: selectedStore || undefined,
          limit: 500
        })
      ]);

      if (summaryRes.data.success) {
        setSummary(summaryRes.data.summary);
        setLastUpdated(new Date(summaryRes.data.lastUpdated));
      }

      if (screensRes.data.success) {
        setScreens(screensRes.data.screens || []);
        setScheduleAccuracySummary(screensRes.data.scheduleAccuracySummary || null);
        setPlaybackStats(screensRes.data.playbackStats || null);
        setScheduleIssuesSummary(screensRes.data.scheduleIssuesSummary || null);

        // Track cache status
        setFromCache(screensRes.data.fromCache || false);
        if (screensRes.data.lastUpdated) {
          setCacheLastUpdated(new Date(screensRes.data.lastUpdated));
        }
      }

    } catch (err) {
      console.error('Fetch data error:', err);
      setError(err.response?.data?.error || 'Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Refresh when store changes (only if company is selected)
  useEffect(() => {
    if (selectedCompany && selectedStore !== undefined) {
      fetchData();
    }
  }, [selectedStore]);

  // Auto-refresh logic
  useEffect(() => {
    if (!autoRefreshEnabled || !selectedCompany) {
      setCountdown(autoRefreshInterval);
      return;
    }

    // Countdown timer
    const countdownTimer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          fetchData();
          return autoRefreshInterval;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, [autoRefreshEnabled, autoRefreshInterval, selectedCompany, selectedStore]);

  // Reset countdown when interval changes
  useEffect(() => {
    setCountdown(autoRefreshInterval);
  }, [autoRefreshInterval]);

  // Detect new issues and show notification
  useEffect(() => {
    if (summary && previousIssueCount !== null) {
      const currentIssueCount = (summary.offline || 0) + (summary.contentIssuesCount || 0) + (summary.scheduleIssuesCount || 0);
      const prevCount = previousIssueCount;

      if (currentIssueCount > prevCount) {
        setNewIssuesDetected(true);
        setCriticalAlertDismissed(false);
        // Play notification sound (optional)
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU');
          audio.volume = 0.3;
          audio.play().catch(() => {}); // Ignore errors if audio can't play
        } catch (e) {}
      }
    }

    if (summary) {
      const currentIssueCount = (summary.offline || 0) + (summary.contentIssuesCount || 0) + (summary.scheduleIssuesCount || 0);
      setPreviousIssueCount(currentIssueCount);
    }
  }, [summary]);

  // Filter and sort screens (flat list, matching Pickcel UI)
  const filteredScreens = useMemo(() => {
    let filtered = [...screens];

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(s =>
        filterStatus === 'online' ? s.status === 'online' : s.status === 'offline'
      );
    }

    // Apply content filter (now uses real playback verification)
    if (filterContent !== 'all') {
      filtered = filtered.filter(s => {
        // Use new isContentError field for actual errors (excludes "No content assigned")
        const hasIssue = s.isContentError === true;
        return filterContent === 'issues' ? hasIssue : !hasIssue;
      });
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        s.name?.toLowerCase().includes(term) ||
        s.storeCode?.toLowerCase().includes(term) ||
        s.storeName?.toLowerCase().includes(term) ||
        s.activeSchedule?.name?.toLowerCase().includes(term) ||
        s.tags?.some(t => t.toLowerCase().includes(term)) ||
        s.associatedProducts?.some(p => p.toLowerCase().includes(term))
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'lastSeen':
          aVal = new Date(a.lastSyncTime || 0).getTime();
          bVal = new Date(b.lastSyncTime || 0).getTime();
          break;
        case 'schedule':
          aVal = a.activeSchedule?.name || '';
          bVal = b.activeSchedule?.name || '';
          break;
        case 'store':
        default:
          aVal = a.storeCode || '';
          bVal = b.storeCode || '';
          break;
      }

      if (typeof aVal === 'string') {
        const compare = aVal.localeCompare(bVal);
        return sortDirection === 'asc' ? compare : -compare;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [screens, filterStatus, filterContent, searchTerm, sortField, sortDirection]);

  // Get screens for modal based on type
  const getModalScreens = useMemo(() => {
    if (!modalType) return [];

    switch (modalType) {
      case 'online':
        return screens.filter(s => s.status === 'online');
      case 'offline':
        return screens.filter(s => s.status === 'offline');
      case 'content':
        // Use new isContentError field for actual errors (excludes "No content assigned")
        return screens.filter(s => s.isContentError === true);
      case 'schedule':
        return screens.filter(s => {
          if (!s.activeSchedule) return false;
          const now = new Date();
          const end = new Date(s.activeSchedule.endTime);
          const start = new Date(s.activeSchedule.startTime);
          return now > end || now < start;
        });
      case 'total':
        return screens;
      case 'playback_verified':
        return screens.filter(s => s.playbackVerified === true);
      // Accuracy card types
      case 'on_schedule':
        return screens.filter(s => s.scheduleAccuracy === 'on_schedule');
      case 'off_schedule':
        return screens.filter(s => s.scheduleAccuracy === 'off_schedule');
      case 'syncing':
        return screens.filter(s => s.scheduleAccuracy === 'syncing');
      case 'default_playing':
        return screens.filter(s => s.scheduleAccuracy === 'default_playing');
      case 'no_content':
        return screens.filter(s => s.scheduleAccuracy === 'no_content');
      case 'not_playing':
        return screens.filter(s => s.scheduleAccuracy === 'not_playing');
      case 'synced_offline':
        return screens.filter(s => s.scheduleAccuracy === 'synced_offline');
      default:
        return [];
    }
  }, [screens, modalType]);

  // Handle accuracy card click
  const handleAccuracyClick = (type, title, count) => {
    if (count === 0) return; // Don't open modal for empty categories
    setModalType(type);
    setModalTitle(title);
    setModalOpen(true);
  };

  // Save alert settings
  const handleSaveAlert = async () => {
    if (!alertSettings.email || !selectedCompany) return;

    setSavingAlert(true);
    try {
      await axiosInstance.post('/digital-signage/alerts/subscribe', {
        company: selectedCompany.companyCode,
        store: selectedStore || null,
        email: alertSettings.email,
        alertOffline: alertSettings.alertOffline,
        alertContentIssues: alertSettings.alertContentIssues,
        alertScheduleIssues: alertSettings.alertScheduleIssues,
        threshold: alertSettings.threshold
      });
      setAlertSaved(true);
      setTimeout(() => {
        setAlertModalOpen(false);
        setAlertSaved(false);
      }, 2000);
    } catch (err) {
      console.error('Error saving alert:', err);
      setError('Failed to save alert settings');
    } finally {
      setSavingAlert(false);
    }
  };

  // Force refresh cache (triggers backend to refresh from Pickcel API)
  const handleForceRefresh = async () => {
    if (!selectedCompany || refreshingCache) return;

    setRefreshingCache(true);
    setJustRefreshed(false);
    try {
      // Wait for cache refresh to complete
      const refreshResponse = await axiosInstance.post('/digital-signage/screens/cache/refresh', {
        company: selectedCompany.companyCode,
        waitForCompletion: true
      });

      if (refreshResponse.data.success && refreshResponse.data.refreshed) {
        // Cache was refreshed - now fetch fresh data
        await fetchData();
        // Mark as just refreshed (not from stale cache)
        setJustRefreshed(true);
        setFromCache(false);
        // Reset the countdown timer since we just got fresh data
        setCountdown(autoRefreshInterval);
      }
    } catch (err) {
      console.error('Error forcing cache refresh:', err);
    } finally {
      setRefreshingCache(false);
    }
  };

  // Load user's alert subscriptions
  const loadUserAlerts = async () => {
    setLoadingAlerts(true);
    try {
      const response = await axiosInstance.get('/digital-signage/alerts');
      setUserAlerts(response.data.alerts || []);
    } catch (err) {
      console.error('Error loading alerts:', err);
      setUserAlerts([]);
    } finally {
      setLoadingAlerts(false);
    }
  };

  // Delete an alert subscription
  const handleDeleteAlert = async (company, store) => {
    const key = `${company}-${store || 'all'}`;
    setDeletingAlert(key);
    try {
      const params = store ? `?store=${encodeURIComponent(store)}` : '';
      await axiosInstance.delete(`/digital-signage/alerts/${encodeURIComponent(company)}${params}`);
      // Refresh the list
      await loadUserAlerts();
    } catch (err) {
      console.error('Error deleting alert:', err);
      setError('Failed to delete alert');
    } finally {
      setDeletingAlert(null);
    }
  };

  // Open manage alerts modal
  const openManageAlerts = () => {
    loadUserAlerts();
    setManageAlertsModalOpen(true);
  };

  // Handle summary card click
  const handleSummaryClick = (type, title) => {
    setModalType(type);
    setModalTitle(title);
    setModalOpen(true);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle auto-refresh interval change
  const handleIntervalChange = async (e) => {
    const newInterval = Number(e.target.value);
    setAutoRefreshInterval(newInterval);
    setCountdown(newInterval);

    // If super admin, save to backend (applies to all users)
    if (user?.isSuperAdmin) {
      try {
        await axiosInstance.post('/digital-signage/settings/auto-refresh-interval', {
          interval: newInterval
        });
        console.log(`Auto-refresh interval set to ${newInterval}s for all users`);
      } catch (err) {
        console.error('Error saving auto-refresh interval:', err);
        setError('Failed to save auto-refresh interval setting');
      }
    }
  };

  // Format time for schedule end date (like "19 Nov, 2025")
  const formatScheduleDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  // Format cache age - shows "Xs", "Xm", or "Xm Xs"
  const formatCacheAge = (date) => {
    if (!date) return 'N/A';
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const remainSec = diffSec % 60;

    if (diffMin === 0) {
      return `${diffSec}s`;
    } else if (diffMin < 60) {
      return remainSec > 0 ? `${diffMin}m ${remainSec}s` : `${diffMin}m`;
    } else {
      const diffHr = Math.floor(diffMin / 60);
      const remainMin = diffMin % 60;
      return remainMin > 0 ? `${diffHr}h ${remainMin}m` : `${diffHr}h`;
    }
  };

  // Format last seen time - matches Pickcel format
  const formatLastSeen = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    const timeStr = date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    if (isToday) {
      return timeStr;
    }

    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month}, ${year} ${timeStr}`;
  };

  // Check if schedule is expired or not started
  const getScheduleStatus = (screen) => {
    if (!screen.activeSchedule) return { status: 'none', message: 'No schedule' };

    const now = new Date();
    const start = new Date(screen.activeSchedule.startTime);
    const end = new Date(screen.activeSchedule.endTime);

    if (now < start) return { status: 'pending', message: 'Not started' };
    if (now > end) return { status: 'expired', message: 'Expired' };
    return { status: 'active', message: 'Active' };
  };

  // Export to CSV - matches Pickcel table columns
  const exportToCSV = () => {
    const headers = ['Screen', 'Status', 'Last Seen', 'Current Schedule', 'Schedule End', 'Currently Playing', 'Schedule Accuracy', 'Store', 'Tags', 'Associated Products'];

    const rows = filteredScreens.map(s => [
      s.name,
      s.status,
      formatLastSeen(s.lastSyncTime),
      s.activeSchedule?.name || 'No Schedule',
      s.activeSchedule ? formatScheduleDate(s.activeSchedule.endTime) : '',
      s.currentlyPlaying?.displayName || s.currentlyPlaying?.name || 'No Content',
      s.scheduleAccuracy || 'Unknown',
      `${s.storeName} (${s.storeCode})`,
      (s.tags || []).join('; '),
      (s.associatedProducts || []).join('; ')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `digital_signage_${selectedCompany?.companyCode || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ProtectedComponent functionCode="SA06" functionName="Digital Signage Maintenance">
      <div className="digital-signage-maintenance">
        {/* Header */}
        <header className="ds-header">
          <div className="header-content">
            <div>
              <h1>Digital Signage Maintenance</h1>
              <p>Monitor device health and content status (Pickcel CMS)</p>
            </div>
            <div className="header-actions">
              {user?.displayName && (
                <span className="user-badge">Logged in as {user.displayName}</span>
              )}
            </div>
          </div>
        </header>

        {/* Selection Controls */}
        <div className="selection-controls">
          <div className="control-group">
            <label>Company (LCD Enabled)</label>
            <select
              value={selectedCompany?.companyCode || ''}
              onChange={handleCompanyChange}
              disabled={loadingLcdCompanies}
              className="company-select"
            >
              <option value="">
                {loadingLcdCompanies ? 'Loading LCD companies...' : 'Select a company'}
              </option>
              {lcdCompanies.map(c => (
                <option key={c.code} value={c.code}>
                  {c.code} - {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label>Store (Optional)</label>
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              disabled={!selectedCompany}
            >
              <option value="">All Stores</option>
              {stores.map(s => (
                <option key={s.storeCode || s.code} value={s.storeCode || s.code}>
                  {s.storeName || s.name || s.storeCode || s.code}
                </option>
              ))}
            </select>
          </div>

          <button
            className="fetch-button"
            onClick={() => fetchData()}
            disabled={!selectedCompany || loading}
          >
            {loading ? 'Loading...' : 'Refresh Data'}
          </button>

          {/* Set Up Alert button */}
          {selectedCompany && (
            <button
              className="alert-setup-button"
              onClick={() => setAlertModalOpen(true)}
            >
              Set Up Alert
            </button>
          )}

          {/* Manage Alerts button */}
          <button
            className="manage-alerts-button"
            onClick={openManageAlerts}
          >
            Manage Alerts
          </button>

          {/* Auto-refresh controls */}
          <div className="auto-refresh-controls">
            <label className="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefreshEnabled}
                onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                disabled={!selectedCompany}
              />
              Auto-refresh
            </label>
            {autoRefreshEnabled && (
              <>
                <select
                  value={autoRefreshInterval}
                  onChange={handleIntervalChange}
                  className="refresh-interval-select"
                  title={user?.isSuperAdmin ? 'Super Admin: Changes apply to all users' : 'Auto-refresh interval (set by admin)'}
                >
                  <option value={30}>30s</option>
                  <option value={60}>1 min</option>
                  <option value={120}>2 min</option>
                  <option value={300}>5 min</option>
                  <option value={600}>10 min</option>
                  <option value={1800}>30 min</option>
                  <option value={3600}>1 hour</option>
                </select>
                {user?.isSuperAdmin && (
                  <span className="admin-indicator" title="Changes apply to all users">
                    ⚙️
                  </span>
                )}
                <span className="countdown-display">
                  Next: {countdown}s
                </span>
              </>
            )}
            {cacheLastUpdated && (
              <span className="last-updated-inline">
                {justRefreshed && <span className="cache-badge fresh">Just Refreshed</span>}
                {fromCache && !justRefreshed && !autoRefreshEnabled && <span className="cache-badge">Cached</span>}
                {autoRefreshEnabled && !justRefreshed && <span className="cache-badge live">Live Data</span>}
                {autoRefreshEnabled ? 'Refreshing in' : 'Data'}: {autoRefreshEnabled ? `${countdown}s` : `${formatCacheAge(cacheLastUpdated)} ago`}
              </span>
            )}
            {selectedCompany && (
              <button
                className="force-refresh-button"
                onClick={handleForceRefresh}
                disabled={refreshingCache || loading}
                title="Force refresh from Pickcel API"
              >
                {refreshingCache ? 'Refreshing...' : 'Refresh Now'}
              </button>
            )}
          </div>
        </div>

        {/* Critical Issues Alert Banner */}
        {summary && !criticalAlertDismissed && ((summary.offline > 0) || (summary.contentIssuesCount > 0) || (summary.scheduleIssuesCount > 0)) && (
          <div className={`critical-alert-banner ${newIssuesDetected ? 'pulse' : ''}`}>
            <div className="alert-icon">!</div>
            <div className="alert-content">
              <strong>Critical Issues Detected</strong>
              <div className="alert-details">
                {summary.offline > 0 && (
                  <span className="alert-item offline" onClick={() => handleSummaryClick('offline', 'Offline Signages')}>
                    {summary.offline} Offline
                  </span>
                )}
                {summary.contentIssuesCount > 0 && (
                  <span className="alert-item content" onClick={() => handleSummaryClick('content', 'Content Issues')}>
                    {summary.contentIssuesCount} Content Issues
                  </span>
                )}
                {summary.scheduleIssuesCount > 0 && (
                  <span className="alert-item schedule" onClick={() => handleSummaryClick('schedule', 'Schedule Issues')}>
                    {summary.scheduleIssuesCount} Schedule Issues
                  </span>
                )}
              </div>
              {newIssuesDetected && (
                <div className="new-issues-notice">New issues detected!</div>
              )}
            </div>
            <button
              className="alert-dismiss"
              onClick={() => {
                setCriticalAlertDismissed(true);
                setNewIssuesDetected(false);
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-banner">{error}</div>
        )}

        {/* Unified Status Cards - All on One Line */}
        {summary && (
          <div className="summary-section">
            <div className="summary-cards">
              {/* Basic Status */}
              <div
                className="summary-card total clickable"
                onClick={() => handleSummaryClick('total', 'All Signages')}
              >
                <div className="card-value">{summary.total}</div>
                <div className="card-label">Total</div>
              </div>
              <div
                className="summary-card online clickable"
                onClick={() => handleSummaryClick('online', 'Online Signages')}
              >
                <div className="card-value">{summary.online}</div>
                <div className="card-label">Online</div>
              </div>
              <div
                className="summary-card offline clickable"
                onClick={() => handleSummaryClick('offline', 'Offline Signages')}
              >
                <div className="card-value">{summary.offline}</div>
                <div className="card-label">Offline</div>
              </div>
              {/* Playback Status */}
              {scheduleAccuracySummary && (
                <>
                  <div
                    className={`summary-card on-schedule ${scheduleAccuracySummary.onSchedule > 0 ? 'clickable' : ''}`}
                    onClick={() => handleAccuracyClick('on_schedule', 'On Schedule Screens', scheduleAccuracySummary.onSchedule)}
                  >
                    <div className="card-value">{scheduleAccuracySummary.onSchedule}</div>
                    <div className="card-label">On Schedule</div>
                  </div>
                  <div
                    className={`summary-card off-schedule ${scheduleAccuracySummary.offSchedule > 0 ? 'clickable' : ''}`}
                    onClick={() => handleAccuracyClick('off_schedule', 'Off Schedule Screens', scheduleAccuracySummary.offSchedule)}
                  >
                    <div className="card-value">{scheduleAccuracySummary.offSchedule}</div>
                    <div className="card-label">Off Schedule</div>
                  </div>
                  {playbackStats && (
                    <div
                      className="summary-card verified clickable"
                      onClick={() => handleSummaryClick('playback_verified', 'Playback Verified Screens')}
                    >
                      <div className="card-value">{playbackStats.verified}</div>
                      <div className="card-label">Verified</div>
                    </div>
                  )}
                  <div
                    className={`summary-card syncing ${scheduleAccuracySummary.syncing > 0 ? 'clickable' : ''}`}
                    onClick={() => handleAccuracyClick('syncing', 'Syncing Screens', scheduleAccuracySummary.syncing)}
                  >
                    <div className="card-value">{scheduleAccuracySummary.syncing}</div>
                    <div className="card-label">Syncing</div>
                  </div>
                  <div
                    className={`summary-card default ${scheduleAccuracySummary.defaultPlaying > 0 ? 'clickable' : ''}`}
                    onClick={() => handleAccuracyClick('default_playing', 'Default Playing Screens', scheduleAccuracySummary.defaultPlaying)}
                  >
                    <div className="card-value">{scheduleAccuracySummary.defaultPlaying}</div>
                    <div className="card-label">Default</div>
                  </div>
                  {scheduleAccuracySummary.notPlaying > 0 && (
                    <div
                      className="summary-card not-playing clickable"
                      onClick={() => handleAccuracyClick('not_playing', 'Not Playing (Issue)', scheduleAccuracySummary.notPlaying)}
                    >
                      <div className="card-value">{scheduleAccuracySummary.notPlaying}</div>
                      <div className="card-label">Not Playing</div>
                    </div>
                  )}
                  <div
                    className={`summary-card no-content ${scheduleAccuracySummary.noContent > 0 ? 'clickable' : ''}`}
                    onClick={() => handleAccuracyClick('no_content', 'No Content Assigned', scheduleAccuracySummary.noContent)}
                  >
                    <div className="card-value">{scheduleAccuracySummary.noContent}</div>
                    <div className="card-label">No Content</div>
                  </div>
                </>
              )}
              {/* Issue Categories */}
              <div
                className="summary-card issues clickable"
                onClick={() => handleSummaryClick('content', 'Content Issues')}
              >
                <div className="card-value">{summary.contentIssuesCount}</div>
                <div className="card-label">Content Issues</div>
              </div>
              <div
                className="summary-card schedule clickable"
                onClick={() => handleSummaryClick('schedule', 'Schedule Issues')}
              >
                <div className="card-value">{summary.scheduleIssuesCount}</div>
                <div className="card-label">Schedule Issues</div>
              </div>
            </div>
          </div>
        )}

        {/* Filters - show when we have screens */}
        {screens.length > 0 && (
          <div className="filter-bar">
            <div className="filter-group">
              <input
                type="text"
                placeholder="Search screens..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-group">
              <label>Status:</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Content:</label>
              <select value={filterContent} onChange={(e) => setFilterContent(e.target.value)}>
                <option value="all">All</option>
                <option value="issues">With Issues</option>
                <option value="ok">OK</option>
              </select>
            </div>

            <button className="export-button" onClick={exportToCSV}>
              Export CSV
            </button>
          </div>
        )}

        {/* Screens Table - Pickcel-style flat list */}
        {filteredScreens.length > 0 && (
          <div className="screens-section">
            <div className="screens-table-wrapper">
              <table className="screens-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable col-screen">
                      Screen {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('lastSeen')} className="sortable col-lastseen">
                      Last Seen {sortField === 'lastSeen' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('schedule')} className="sortable col-schedule">
                      Current Schedule {sortField === 'schedule' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="col-playing">
                      Currently Playing
                    </th>
                    <th onClick={() => handleSort('store')} className="sortable col-store">
                      Store {sortField === 'store' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="col-tags">Tags</th>
                    <th className="col-products">Associated Products</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredScreens.map(screen => {
                    const scheduleInfo = getScheduleStatus(screen);
                    return (
                      <tr key={screen.id} className={`screen-row ${screen.status}`}>
                        {/* SCREEN column */}
                        <td className="screen-name-cell">
                          <span className={`status-dot ${screen.status}`}></span>
                          <span className="screen-name">{screen.name}</span>
                        </td>

                        {/* LAST SEEN column */}
                        <td className={`last-seen-cell ${screen.status}`}>
                          {formatLastSeen(screen.lastSyncTime)}
                        </td>

                        {/* CURRENT SCHEDULE column */}
                        <td className="schedule-cell">
                          {screen.activeSchedule ? (
                            <div className="schedule-info">
                              <span className="schedule-name">{screen.activeSchedule.name}</span>
                              <span className={`schedule-end ${scheduleInfo.status}`}>
                                {scheduleInfo.status === 'expired' ? 'Expired' : `Till ${formatScheduleDate(screen.activeSchedule.endTime)}`}
                              </span>
                            </div>
                          ) : (
                            <span className="no-schedule">No Schedule</span>
                          )}
                        </td>

                        {/* CURRENTLY PLAYING column */}
                        <td className="playing-cell">
                          {screen.currentlyPlaying ? (
                            <div className={`playing-info ${screen.scheduleAccuracy}`}>
                              <span className="playing-name">{screen.currentlyPlaying.displayName || screen.currentlyPlaying.name || 'Unknown'}</span>
                              <span className={`accuracy-badge ${screen.scheduleAccuracy}`}>
                                {screen.scheduleAccuracy === 'on_schedule' && 'Verified ✓'}
                                {screen.scheduleAccuracy === 'content_mismatch' && 'Wrong Content!'}
                                {screen.scheduleAccuracy === 'updating' && 'Updating...'}
                                {screen.scheduleAccuracy === 'default_playing' && 'Default'}
                                {screen.scheduleAccuracy === 'no_content' && 'No Content'}
                                {screen.scheduleAccuracy === 'not_playing' && 'Not Playing!'}
                                {screen.scheduleAccuracy === 'synced_offline' && 'Synced (Offline)'}
                                {screen.scheduleAccuracy === 'offline_no_sync' && 'Sync Interrupted'}
                                {screen.scheduleAccuracy === 'sync_failed' && 'Sync Failed'}
                                {screen.scheduleAccuracy === 'default_synced' && 'Default Synced'}
                                {screen.scheduleAccuracy === 'unknown' && 'Unknown'}
                              </span>
                              {/* Show content issue if present */}
                              {screen.contentIssue && (
                                <span className="issue-indicator" title={screen.contentIssue}>⚠</span>
                              )}
                            </div>
                          ) : (
                            <span className="no-content">No Content</span>
                          )}
                        </td>

                        {/* STORE column */}
                        <td className="store-cell">
                          <span className="store-display">
                            {screen.storeName} ({screen.storeCode})
                          </span>
                        </td>

                        {/* TAGS column */}
                        <td className="tags-cell">
                          {screen.tags && screen.tags.length > 0 ? (
                            <div className="tags-list">
                              {screen.tags.map((tag, i) => (
                                <span key={i} className="tag-badge">{tag}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="no-tags">-</span>
                          )}
                        </td>

                        {/* ASSOCIATED PRODUCTS column */}
                        <td className="products-cell">
                          {screen.associatedProducts && screen.associatedProducts.length > 0 ? (
                            <div className="products-list">
                              {screen.associatedProducts.slice(0, 3).map((productId, i) => (
                                <span key={i} className="product-badge">{productId}</span>
                              ))}
                              {screen.associatedProducts.length > 3 && (
                                <span className="more-products">+{screen.associatedProducts.length - 3} more</span>
                              )}
                            </div>
                          ) : (
                            <span className="no-products">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}


        {/* Empty State */}
        {!loading && selectedCompany && screens.length === 0 && !error && (
          <div className="empty-state">
            <p>No signage data found for the selected company.</p>
            <p>Click "Refresh Data" to fetch the latest information.</p>
          </div>
        )}

        {/* Initial State */}
        {!loading && !selectedCompany && !error && (
          <div className="empty-state">
            <p>Select a company above to view digital signage status.</p>
          </div>
        )}

        {/* Summary Detail Modal */}
        {modalOpen && (
          <div className="modal-overlay" onClick={() => setModalOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{modalTitle}</h2>
                <button className="modal-close" onClick={() => setModalOpen(false)}>×</button>
              </div>
              <div className="modal-body">
                {/* Schedule Issues Summary Banner */}
                {modalType === 'schedule' && scheduleIssuesSummary && (
                  <div className="schedule-issues-summary-banner">
                    <div className="issue-breakdown">
                      {scheduleIssuesSummary.expiredError > 0 && (
                        <span className="issue-count error">
                          {scheduleIssuesSummary.expiredError} Expired (Critical)
                        </span>
                      )}
                      {scheduleIssuesSummary.expiredWarning > 0 && (
                        <span className="issue-count warning">
                          {scheduleIssuesSummary.expiredWarning} Expired (Recent)
                        </span>
                      )}
                      {scheduleIssuesSummary.notStarted > 0 && (
                        <span className="issue-count info">
                          {scheduleIssuesSummary.notStarted} Not Started Yet
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {getModalScreens.length === 0 ? (
                  <p className="no-data">No screens in this category.</p>
                ) : (
                  <div className="modal-table-wrapper">
                    <table className="modal-table">
                      <thead>
                        <tr>
                          <th>Screen</th>
                          <th>Status</th>
                          <th>Last Seen</th>
                          {modalType === 'schedule' ? (
                            <>
                              <th>Schedule Issue</th>
                              <th>Schedule Details</th>
                            </>
                          ) : (
                            <>
                              <th>Currently Playing</th>
                              <th>Schedule</th>
                            </>
                          )}
                          <th>Store</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getModalScreens.map(screen => (
                          <tr key={screen.id} className={modalType === 'schedule' && screen.scheduleIssue ? `schedule-issue-row ${screen.scheduleIssue.severity}` : ''}>
                            <td>
                              <span className={`status-dot ${screen.status}`}></span>
                              {screen.name}
                            </td>
                            <td className={screen.status}>{screen.status}</td>
                            <td>{formatLastSeen(screen.lastSyncTime)}</td>
                            {modalType === 'schedule' ? (
                              <>
                                <td className="schedule-issue-cell">
                                  {screen.scheduleIssue ? (
                                    <div className={`schedule-issue-badge ${screen.scheduleIssue.type} ${screen.scheduleIssue.severity}`}>
                                      <span className="issue-icon">
                                        {screen.scheduleIssue.severity === 'error' ? '!' :
                                         screen.scheduleIssue.type === 'not_started' ? '?' : '!'}
                                      </span>
                                      <span className="issue-text">
                                        {screen.scheduleIssue.type === 'expired' ? 'EXPIRED' : 'NOT STARTED'}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="no-issue">OK</span>
                                  )}
                                </td>
                                <td className="schedule-details-cell">
                                  {screen.scheduleIssue ? (
                                    <div className="schedule-issue-details">
                                      <span className="issue-message">{screen.scheduleIssue.message}</span>
                                      <span className="schedule-name">{screen.activeSchedule?.name}</span>
                                      {screen.scheduleIssue.type === 'expired' && (
                                        <span className="date-info">End: {formatScheduleDate(screen.activeSchedule?.endTime)}</span>
                                      )}
                                      {screen.scheduleIssue.type === 'not_started' && (
                                        <span className="date-info">Start: {formatScheduleDate(screen.activeSchedule?.startTime)}</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span>{screen.activeSchedule?.name || 'No Schedule'}</span>
                                  )}
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="modal-playing-cell">
                                  {screen.currentlyPlaying ? (
                                    <div className="modal-playing-info">
                                      <span className="modal-playing-name">{screen.currentlyPlaying.displayName || screen.currentlyPlaying.name || 'Unknown'}</span>
                                      {screen.playbackVerified && <span className="modal-verified-badge">Verified</span>}
                                    </div>
                                  ) : (
                                    <span className="no-content">No Content</span>
                                  )}
                                </td>
                                <td>{screen.activeSchedule?.name || 'No Schedule'}</td>
                              </>
                            )}
                            <td>{screen.storeName} ({screen.storeCode})</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Alert Setup Modal */}
        {alertModalOpen && (
          <div className="modal-overlay" onClick={() => setAlertModalOpen(false)}>
            <div className="modal-content alert-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Set Up Email Alerts</h2>
                <button className="modal-close" onClick={() => setAlertModalOpen(false)}>×</button>
              </div>
              <div className="modal-body">
                {alertSaved ? (
                  <div className="alert-saved-message">
                    <span className="success-icon">✓</span>
                    Alert settings saved successfully!
                  </div>
                ) : (
                  <div className="alert-form">
                    <p className="alert-form-description">
                      Get notified when issues are detected for <strong>{selectedCompany?.companyName}</strong>
                      {selectedStore && <> (Store: {selectedStore})</>}
                    </p>

                    <div className="form-group">
                      <label>Email Address</label>
                      <input
                        type="email"
                        placeholder="your.email@company.com"
                        value={alertSettings.email}
                        onChange={(e) => setAlertSettings(prev => ({ ...prev, email: e.target.value }))}
                      />
                    </div>

                    <div className="alert-options">
                      <label className="alert-option">
                        <input
                          type="checkbox"
                          checked={alertSettings.alertOffline}
                          onChange={(e) => setAlertSettings(prev => ({ ...prev, alertOffline: e.target.checked }))}
                        />
                        <span className="option-label">
                          <strong>Offline Alerts</strong>
                          <small>Get notified when signages go offline</small>
                        </span>
                      </label>

                      <label className="alert-option">
                        <input
                          type="checkbox"
                          checked={alertSettings.alertContentIssues}
                          onChange={(e) => setAlertSettings(prev => ({ ...prev, alertContentIssues: e.target.checked }))}
                        />
                        <span className="option-label">
                          <strong>Content Issues</strong>
                          <small>Get notified when wrong content is playing</small>
                        </span>
                      </label>

                      <label className="alert-option">
                        <input
                          type="checkbox"
                          checked={alertSettings.alertScheduleIssues}
                          onChange={(e) => setAlertSettings(prev => ({ ...prev, alertScheduleIssues: e.target.checked }))}
                        />
                        <span className="option-label">
                          <strong>Schedule Issues</strong>
                          <small>Get notified about expired or missing schedules</small>
                        </span>
                      </label>
                    </div>

                    <div className="alert-form-actions">
                      <button
                        className="cancel-button"
                        onClick={() => setAlertModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="save-alert-button"
                        onClick={handleSaveAlert}
                        disabled={!alertSettings.email || savingAlert || (!alertSettings.alertOffline && !alertSettings.alertContentIssues && !alertSettings.alertScheduleIssues)}
                      >
                        {savingAlert ? 'Saving...' : 'Save Alert Settings'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Manage Alerts Modal */}
        {manageAlertsModalOpen && (
          <div className="modal-overlay" onClick={() => setManageAlertsModalOpen(false)}>
            <div className="modal-content manage-alerts-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Manage Alert Subscriptions</h2>
                <button className="modal-close" onClick={() => setManageAlertsModalOpen(false)}>×</button>
              </div>
              <div className="modal-body">
                {loadingAlerts ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading your alerts...</p>
                  </div>
                ) : userAlerts.length === 0 ? (
                  <div className="empty-state">
                    <p>You don't have any alert subscriptions yet.</p>
                    <p>Click "Set Up Alert" to create one.</p>
                  </div>
                ) : (
                  <div className="alerts-list">
                    <table className="modal-table">
                      <thead>
                        <tr>
                          <th>Company</th>
                          <th>Store</th>
                          <th>Email</th>
                          <th>Alert Types</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userAlerts.map((alert, idx) => {
                          const alertKey = `${alert.company}-${alert.store || 'all'}`;
                          const alertTypes = [];
                          if (alert.alertOffline) alertTypes.push('Offline');
                          if (alert.alertContentIssues) alertTypes.push('Content');
                          if (alert.alertScheduleIssues) alertTypes.push('Schedule');

                          return (
                            <tr key={idx}>
                              <td><strong>{alert.company}</strong></td>
                              <td>{alert.store || 'All Stores'}</td>
                              <td>{alert.email}</td>
                              <td>
                                <div className="alert-types-badges">
                                  {alertTypes.map((type, i) => (
                                    <span key={i} className={`alert-type-badge ${type.toLowerCase()}`}>
                                      {type}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <button
                                  className="delete-alert-button"
                                  onClick={() => handleDeleteAlert(alert.company, alert.store)}
                                  disabled={deletingAlert === alertKey}
                                >
                                  {deletingAlert === alertKey ? 'Deleting...' : 'Delete'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </ProtectedComponent>
  );
}

export default DigitalSignageMaintenance;
